function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useCallback, useMemo } from 'react';
import { Box, Image } from 'grommet';
import styled from 'styled-components';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { EditorState, convertToRaw } from 'draft-js';
import 'draft-js/dist/Draft.css';
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import '@draft-js-plugins/mention/lib/plugin.css';
const StyledMentionBox = styled(Box)`
  .DraftEditor-root {
    border: 1px solid #ddd;
    padding: 5px;
    border-radius: 3px;
    background: #fff;
    font-size: 12px;
  }
  .DraftEditor-editorContainer {
    cursor: text;
    min-height: 40px;
  }
`;
const ImageBox = styled(Box)`
  padding: 0 10px 0 0;
`;
const StyledImage = styled(Image)`
  display: inline-block;
  width: 27px;
  height: 27px;
  border-radius: 50%;
`;
const NameBox = styled(Box)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  width: 150px;
  line-height: 1.5;
`;
const TitleBox = styled(Box)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  color: #a7a7a7;
  width: 150px;
  line-height: 1.5;
`;
const ParentBox = styled(Box)`
  max-width: 270px;
  padding: 5px 0 0 10px;
`;
const Entry = props => {
  const {
    mention,
    ...parentProps
  } = props;
  return /*#__PURE__*/React.createElement(ParentBox, parentProps, /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    style: {
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement(ImageBox, null, /*#__PURE__*/React.createElement(StyledImage, {
    src: mention.avatar
  })), /*#__PURE__*/React.createElement(Box, {
    direction: "column"
  }, /*#__PURE__*/React.createElement(NameBox, null, mention.name), /*#__PURE__*/React.createElement(TitleBox, null, mention.title))));
};
const MentionBox = ({
  className,
  name,
  setFieldValue,
  setMentions,
  userRoles,
  userList,
  fetchUsers,
  onChange: handleOnChange,
  editorProps = {}
}) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [suggestions, setSuggestions] = useState([...userList, ...userRoles]);
  const [usersData, setUsersData] = useState(userList);
  const [open, setOpen] = useState(false);
  const {
    MentionSuggestions,
    plugins
  } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      mentionPrefix: '@',
      supportWhitespace: true
    });
    const {
      MentionSuggestions
    } = mentionPlugin;
    const plugins = [mentionPlugin];
    return {
      plugins,
      MentionSuggestions
    };
  }, []);
  const getMentions = useCallback(editorState => {
    const raw = convertToRaw(editorState.getCurrentContent());
    let mentions = [];
    for (let key in raw.entityMap) {
      const ent = raw.entityMap[key];
      if (ent.type === 'mention') mentions.push(ent.data.mention);
    }
    setMentions(mentions);
  }, [setMentions]);
  const onChange = useCallback(editorState => {
    setEditorState(editorState);

    // get editor text
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
    let noteText = blocks.map(block => !block.text.trim() && '\n' || block.text).join('\n');
    if (!/./.test(noteText)) noteText = '';
    setFieldValue(name, noteText);
    handleOnChange(noteText);

    // get mentions
    getMentions(editorState);
  }, [name, setFieldValue, getMentions, handleOnChange]);
  const fetchOptions = async value => {
    let mentions;
    if (value && defaultSuggestionsFilter(value, [...usersData, ...userRoles]).length < 20) {
      let data = await fetchUsers(value);
      let allUsers = [...usersData, ...data];
      allUsers = _.uniqBy(allUsers, 'pk');
      mentions = [...allUsers, ...userRoles];
      setSuggestions(defaultSuggestionsFilter(value, mentions));
      setUsersData(allUsers);
    } else {
      mentions = [...usersData, ...userRoles];
      setSuggestions(defaultSuggestionsFilter(value, mentions));
    }
  };
  const debounce = (func, delay) => {
    let timerId;
    return function ({
      value
    }) {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        func(value);
      }, delay);
    };
  };
  const onSearchChange = useCallback(debounce(fetchOptions, 500), []);
  const onOpenChange = useCallback(open => {
    setOpen(open);
  }, []);
  return /*#__PURE__*/React.createElement(StyledMentionBox, {
    className: className
  }, /*#__PURE__*/React.createElement(Editor, _extends({
    editorKey: 'editor',
    editorState: editorState,
    onChange: onChange,
    plugins: plugins,
    defaultKeyCommands: false
  }, editorProps)), /*#__PURE__*/React.createElement(MentionSuggestions, {
    suggestions: suggestions,
    onSearchChange: onSearchChange,
    open: open,
    onOpenChange: onOpenChange,
    entryComponent: Entry
  }));
};
Entry.propTypes = {
  mention: PropTypes.object
};
MentionBox.defaultProps = {
  name: 'mentionBox',
  setFieldValue: () => {},
  setMentions: () => {},
  userRoles: [],
  userList: [],
  fetchUsers: () => [],
  onChange: () => {}
};
MentionBox.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  setFieldValue: PropTypes.func,
  setMentions: PropTypes.func,
  userRoles: PropTypes.array,
  userList: PropTypes.array,
  fetchUsers: PropTypes.func,
  editorProps: PropTypes.object,
  onChange: PropTypes.func,
  placeholder: PropTypes.string
};
export default MentionBox;